/**
 * What the Diagnostics panel needs to know about the walk that is open (or was last): a few
 * `diagWalk*` calls from main.ts and one `diagFrameOpened` from PlayerScreen keep this up to date.
 * Each call also leaves a lifecycle mark in the event log.
 */
import { diagHookViewer, diagMark } from "./capture";
import { pathOnly } from "./redact";

export interface WalkMark {
  id: string;
  name: string;
  gpuMb?: number;
  preset?: string;
  openStartedAt: number;
  /** When the Player handed the walk to the viewer iframe. */
  shownAt: number | null;
  closedAt: number | null;
}

export interface FrameMark {
  el: HTMLIFrameElement;
  src: string;
  createdAt: number;
  loadedAt: number | null;
  /** What the load event saw: "same-origin ..." or why the document was not reachable. */
  loadNote: string;
  /** Set once the walk is closed (the iframe is gone). */
  closed: boolean;
}

export const diagState: { walk: WalkMark | null; frame: FrameMark | null } = { walk: null, frame: null };

export function diagWalkOpening(info: { id: string; name: string; gpuMb?: number; preset?: string }): void {
  diagState.walk = { id: info.id, name: info.name, gpuMb: info.gpuMb, preset: info.preset, openStartedAt: Date.now(), shownAt: null, closedAt: null };
  diagMark("walk-open", `${info.name} gpuMb=${info.gpuMb ?? "n/a"} preset=${info.preset ?? "n/a"}`);
}

export function diagWalkShown(): void {
  if (diagState.walk) diagState.walk.shownAt = Date.now();
  diagMark("walk-shown", "iframe handed the viewer url");
}

export function diagWalkFailed(message: string): void {
  diagMark("walk-failed", message);
}

export function diagWalkClosed(): void {
  if (diagState.walk && diagState.walk.closedAt === null) {
    diagState.walk.closedAt = Date.now();
    diagMark("walk-close", `${diagState.walk.name} after ${Math.round((diagState.walk.closedAt - diagState.walk.openStartedAt) / 1000)} s`);
  }
  if (diagState.frame) diagState.frame.closed = true;
}

/** The viewer reported ready or an error of its own (PlayerScreen.watchLoad -> main.ts). */
export function diagViewerSettled(): void {
  const viewer = (diagState.frame && !diagState.frame.closed ? diagState.frame.el.contentWindow : null) as unknown as
    | { walkbake?: { ready?: boolean; error?: unknown } }
    | null;
  let state = "?";
  try {
    state = viewer?.walkbake?.error ? `error: ${String(viewer.walkbake.error).slice(0, 120)}` : viewer?.walkbake?.ready ? "ready" : "not ready";
  } catch {
    state = "not reachable";
  }
  diagMark("viewer", state);
}

/** Called by PlayerScreen.show() right after it set frame.src. */
export function diagFrameOpened(frame: HTMLIFrameElement): void {
  const mark: FrameMark = { el: frame, src: frame.src, createdAt: Date.now(), loadedAt: null, loadNote: "no load event yet", closed: false };
  diagState.frame = mark;
  diagMark("iframe", `src ${pathOnly(frame.src)}`);
  frame.addEventListener("load", () => {
    if (mark.closed || diagState.frame !== mark) return; // the about:blank load after hide()
    mark.loadedAt = Date.now();
    diagHookViewer(frame.contentWindow);
    try {
      const doc = frame.contentDocument;
      if (!doc) throw new Error("contentDocument is null");
      mark.loadNote = `same-origin, ${pathOnly(doc.location.href)}, readyState=${doc.readyState}, title="${doc.title.slice(0, 60)}"`;
    } catch (err) {
      mark.loadNote = `NOT reachable (${(err as Error).name}): cross-origin or a browser error page`;
    }
    diagMark("iframe-load", mark.loadNote);
  });
  frame.addEventListener("error", () => diagMark("iframe-error", "error event on the iframe"));
}
